// batch-69 magazijn-cijfers (FO-521 §A) — pure rekenregels voor de voorraadwaarde.
//
// Géén DB-calls. De waarde-export, het waarde-overzicht op /voorraad/rapportages
// en de KPI "Voorraadwaarde" delen deze functies, zodat de drie per definitie op
// elkaar sluiten. Geld rekent exact (decimaal-strings, zie besteladvies.ts); pas
// de presentatie rondt af op centen.
//
// Voorraadwaarde (spec 221 A1, gelijk aan de bestaande KPI):
//   bulk  = SUM(saldo x prijs)            prijs = artikelprijs of laatste orderprijs
//   uniek = SUM(aanschafbedrag)           status niet AFGESCHREVEN
// Een regel zonder prijs telt NIET mee in de waarde maar wel in "zonder prijs".

import {
  eenhedenNaarGeld,
  geldNaarEenheden,
  telGeldOp,
  vermenigvuldigGeld,
} from './besteladvies'
// Hele kalendermaanden: dezelfde regel als de ketenregeling (één implementatie).
import { heleMaandenTussen } from './ketenregeling'

export type Waarderingsgrondslag = 'ARTIKELPRIJS' | 'LAATSTE_ORDERPRIJS'
export type WaardePrijsBron = 'ARTIKEL' | 'ORDER'

/**
 * De prijs waarmee een bulk-regel gewaardeerd wordt. Bij LAATSTE_ORDERPRIJS de
 * prijs van de laatste inkooporderregel, met terugval op de artikelprijs (spec
 * 221 A2); bij ARTIKELPRIJS altijd de artikelprijs. Een orderprijs van 0 telt als
 * onbekend (een automatische order zonder prijs schrijft 0; dat is geen prijs).
 */
export function kiesWaarderingsprijs(
  artikelPrijs: string | null,
  laatsteOrderPrijs: string | null,
  grondslag: Waarderingsgrondslag,
): { prijs: string | null; bron: WaardePrijsBron | null } {
  if (grondslag === 'LAATSTE_ORDERPRIJS' && laatsteOrderPrijs != null && geldNaarEenheden(laatsteOrderPrijs) > BigInt(0)) {
    return { prijs: laatsteOrderPrijs, bron: 'ORDER' }
  }
  if (artikelPrijs != null) return { prijs: artikelPrijs, bron: 'ARTIKEL' }
  return { prijs: null, bron: null }
}

export interface WaardeBulkRegel {
  aantal: number
  prijs: string | null
}

export interface WaardeUniekRegel {
  aanschafBedrag: string | null
}

/**
 * Waarde van één bulk-regel (saldo x prijs), of null zonder prijs. Een negatief
 * saldo telt als 0: een negatieve voorraadwaarde bestaat niet (de regel laat het
 * saldo zelf wel zien).
 */
export function bulkRegelWaarde(r: WaardeBulkRegel): string | null {
  return r.prijs == null ? null : vermenigvuldigGeld(r.prijs, Math.max(0, r.aantal))
}

export interface VoorraadWaardeTotaal {
  /** Exacte som (decimaal-string, 4 decimalen). */
  waarde: string
  bulkWaarde: string
  uniekWaarde: string
  /** Bulk-stuks (som saldo) en unieke exemplaren. */
  stuks: number
  exemplaren: number
  /** Regels (bulk) met een saldo boven 0 maar zonder prijs; exemplaren zonder aanschafbedrag. */
  bulkZonderPrijs: number
  exemplarenZonderPrijs: number
}

/**
 * Tel bulk-regels en exemplaren op tot één totaal. Een bulk-regel met saldo 0
 * telt niet als "zonder prijs" (er ligt niets dat waarde mist).
 */
export function berekenVoorraadWaarde(
  bulk: readonly WaardeBulkRegel[],
  uniek: readonly WaardeUniekRegel[],
): VoorraadWaardeTotaal {
  const bulkBedragen: string[] = []
  let stuks = 0
  let bulkZonderPrijs = 0
  for (const r of bulk) {
    stuks += Math.max(0, r.aantal)
    const w = bulkRegelWaarde(r)
    if (w == null) {
      if (r.aantal > 0) bulkZonderPrijs++
    } else bulkBedragen.push(w)
  }
  const uniekBedragen: string[] = []
  let exemplarenZonderPrijs = 0
  for (const u of uniek) {
    if (u.aanschafBedrag == null) exemplarenZonderPrijs++
    else uniekBedragen.push(u.aanschafBedrag)
  }
  const bulkWaarde = telGeldOp(bulkBedragen)
  const uniekWaarde = telGeldOp(uniekBedragen)
  return {
    waarde: telGeldOp([bulkWaarde, uniekWaarde]),
    bulkWaarde,
    uniekWaarde,
    stuks,
    exemplaren: uniek.length,
    bulkZonderPrijs,
    exemplarenZonderPrijs,
  }
}

/**
 * Aandeel van een deel in het geheel, in procenten met één decimaal. Een geheel
 * van 0 geeft 0 (geen deling door nul).
 */
export function waardePercentage(deel: string, geheel: string): number {
  const g = geldNaarEenheden(geheel)
  if (g === BigInt(0)) return 0
  // Promille met afronding half-up, dan /10: exact tot op één decimaal.
  const promille = (geldNaarEenheden(deel) * BigInt(2000) + g) / (g * BigInt(2))
  return Number(promille) / 10
}

/**
 * Boekwaarde bij lineaire afschrijving (spec 221 A3): aanschafbedrag x (1 -
 * verstreken hele maanden / termijn), nooit onder 0. Zonder aankoopdatum, zonder
 * termijn of zonder aanschafbedrag: onbekend (null). De peildatum is een
 * NL-kalenderdag (yyyy-mm-dd).
 */
export function berekenBoekwaarde(
  aanschafBedrag: string | null,
  aankoopdatumYmd: string | null,
  peilYmd: string,
  termijnMaanden: number | null | undefined,
): string | null {
  if (aanschafBedrag == null || !aankoopdatumYmd || !termijnMaanden || termijnMaanden <= 0) return null
  const maanden = heleMaandenTussen(ymdNaarUtc(aankoopdatumYmd), ymdNaarUtc(peilYmd))
  if (maanden <= 0) return eenhedenNaarGeld(geldNaarEenheden(aanschafBedrag))
  if (maanden >= termijnMaanden) return eenhedenNaarGeld(BigInt(0))
  const resterend = BigInt(termijnMaanden - maanden)
  // aanschaf x resterend / termijn, half-up op tienduizendsten.
  const t = BigInt(termijnMaanden)
  const teller = geldNaarEenheden(aanschafBedrag) * resterend * BigInt(2) + t
  return eenhedenNaarGeld(teller / (t * BigInt(2)))
}

function ymdNaarUtc(ymd: string): Date {
  const [j, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(j!, m! - 1, d!))
}
